import { Box, InputAdornment, Stack, Typography } from '@mui/material';
import {
  BenefitDTOBenefitEnum,
  CreateBenefitActivationDTO,
  LunchBenefitOptionsDTOTaxationTypeEnum,
  MobilityBenefitOptionsDTOTaxationTypeEnum,
  TenantDTO,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import { RadioControl } from 'probonio-shared-ui/component/form';
import { apis } from 'probonio-shared-ui/module/api';
import { useTenant } from 'probonio-shared-ui/module/me';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { TextFieldControl } from '../../../../component/form';
import { BenefitActivationDialogExtensionInterface } from '../../../../extension/interface/benefit-activation-dialog.extension.interface';
import { ActivationDialogExtensions } from '../../ActivationDialog';
import { NonCashLimitWarning } from '../../ActivationDialog/NonCashLimitWarning';
import { NonCashAvailableCouponsAccordion } from '../../nonCashBenefit/NonCashAvailableCouponsAccordion';
import { SelectNonCashBenefitsForm } from '../../nonCashBenefit/SelectNonCashBenefitsForm';
import { NonCashBenefit } from '../../nonCashBenefit/const';
export interface FlexBenefitOptions {
  monthlyBudget: number;
  lunchTaxationType: LunchBenefitOptionsDTOTaxationTypeEnum;
  mobilityTaxationType: MobilityBenefitOptionsDTOTaxationTypeEnum;
  nonCashBenefits?: NonCashBenefit[];
}

const COUPON_MAX = 50;
const COUPON_STEP = 5;

function getCouponBudget(monthlyBudget: number): number {
  // round to 5€ steps
  return Math.min(COUPON_MAX, Math.trunc(monthlyBudget / COUPON_STEP) * COUPON_STEP) * 100;
}

const Header: React.FC = () => {
  const { tenant } = useTenant();
  const { employeeId } = useParams();
  const { watch } = useFormContext();
  const benefitOptions: FlexBenefitOptions = watch('benefitOptions');

  return (
    <>
      {tenant?.flexBenefits?.includes(BenefitDTOBenefitEnum.Coupons) && (
        <NonCashLimitWarning
          employeeId={employeeId}
          benefit={BenefitDTOBenefitEnum.Flex}
          monthlyBudget={getCouponBudget(benefitOptions.monthlyBudget!)}
        />
      )}
    </>
  );
};

const InlineField: React.FC = () => {
  const { t } = useTranslation('benefitsModule');
  const { control } = useFormContext();

  return (
    <TextFieldControl
      slotProps={{
        input: {
          endAdornment: <InputAdornment position="end">€</InputAdornment>,
        },
        htmlInput: { step: '0.01' },
      }}
      rules={{
        required: true,
        min: 5,
        max: 10000,
      }}
      type="number"
      control={control}
      variant="outlined"
      name="benefitOptions.monthlyBudget"
      label={t('FLEX.fields.monthlyBudget')}
      margin="dense"
      fullWidth
    />
  );
};

const Fields: React.FC<{ enforcedNonCashBenefits?: readonly NonCashBenefit[] }> = ({ enforcedNonCashBenefits }) => {
  const { t } = useTranslation('benefitsModule');
  const { control, watch } = useFormContext();
  const { tenant } = useTenant();

  const benefitOptions: FlexBenefitOptions = watch('benefitOptions');

  const couponBudget = getCouponBudget(benefitOptions.monthlyBudget);

  return (
    <Stack spacing={2}>
      {tenant?.flexBenefits?.includes(BenefitDTOBenefitEnum.Lunch) && (
        <Box>
          <Typography variant="h3" gutterBottom>
            {t('common:benefit.LUNCH')}
          </Typography>
          <RadioControl
            control={control}
            name="benefitOptions.lunchTaxationType"
            options={[
              {
                label: t('LUNCH.fields.taxationType.FLAT_RATE_TAXATION'),
                value: LunchBenefitOptionsDTOTaxationTypeEnum.FlatRateTaxation,
              },
              {
                label: t('LUNCH.fields.taxationType.SIMPLE_TAXATION'),
                value: LunchBenefitOptionsDTOTaxationTypeEnum.SimpleTaxation,
              },
            ]}
          />
        </Box>
      )}
      {tenant?.flexBenefits?.includes(BenefitDTOBenefitEnum.Mobility) && (
        <Box>
          <Typography variant="h3" gutterBottom>
            {t('common:benefit.MOBILITY')}
          </Typography>
          <RadioControl
            control={control}
            name="benefitOptions.mobilityTaxationType"
            options={[
              {
                label: t('MOBILITY.fields.taxationType.TAXABLE'),
                value: MobilityBenefitOptionsDTOTaxationTypeEnum.Taxable,
              },
              {
                label: t('MOBILITY.fields.taxationType.TAX_FREE'),
                value: MobilityBenefitOptionsDTOTaxationTypeEnum.TaxFree,
              },
            ]}
          />
        </Box>
      )}
      {!!tenant?.nonCashBenefits.length && (
        <>
          <SelectNonCashBenefitsForm enforcedNonCashBenefits={enforcedNonCashBenefits} />
          <NonCashAvailableCouponsAccordion couponBudget={couponBudget} nonCashBenefits={benefitOptions.nonCashBenefits} />
        </>
      )}
    </Stack>
  );
};
/** Reused by Wellpass membership import (FormProvider with `benefitOptions` only; no start/end dates). */
export { Fields, InlineField };

const resetFlexBenefitOptions: ActivationDialogExtensions<FlexBenefitOptions>['onResetBenefitOptions'] = (
  selectedActivation,
  context,
): FlexBenefitOptions => {
  return {
    monthlyBudget: selectedActivation?.flexOptions ? selectedActivation.flexOptions.monthlyBudget / 100 : 100,
    lunchTaxationType: selectedActivation?.flexOptions
      ? selectedActivation.flexOptions.lunchTaxationType
      : context?.tenant?.lunchTaxationType || LunchBenefitOptionsDTOTaxationTypeEnum.FlatRateTaxation,
    mobilityTaxationType: selectedActivation?.flexOptions?.mobilityTaxationType || MobilityBenefitOptionsDTOTaxationTypeEnum.Taxable,
    nonCashBenefits: selectedActivation?.flexOptions?.nonCashBenefits?.length
      ? selectedActivation.flexOptions.nonCashBenefits
      : (context?.tenant?.nonCashBenefits ?? []),
  };
};

const mapFlexOptions = (benefitOptions: FlexBenefitOptions, tenant?: TenantDTO) => {
  let nonCashBenefits: NonCashBenefit[] | undefined;
  if (tenant?.nonCashBenefits.length) {
    nonCashBenefits = tenant.nonCashBenefits.length === 1 ? tenant.nonCashBenefits : benefitOptions.nonCashBenefits;
  }

  return {
    monthlyBudget: Math.round(benefitOptions.monthlyBudget * 100),
    lunchTaxationType: benefitOptions.lunchTaxationType,
    mobilityTaxationType: benefitOptions.mobilityTaxationType,
    nonCashBenefits,
  };
};

const createFlexBenefitActivation: ActivationDialogExtensions<FlexBenefitOptions>['onCreateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
  context,
) => {
  const create: CreateBenefitActivationDTO = {
    ...baseValues,
    benefit: BenefitDTOBenefitEnum.Flex,
    flexOptions: mapFlexOptions(benefitOptions, context?.tenant),
  };
  await saveActivation(create);

  if (context?.tenant?.lunchTaxationType !== benefitOptions.lunchTaxationType && context?.tenant) {
    await apis.tenants.updateTenant({
      tenantId: context.tenant.id,
      updateTenantDTO: { lunchTaxationType: benefitOptions.lunchTaxationType },
    });
    await context.queryClient.invalidateQueries({ queryKey: ['tenants', context.tenant.id] });
  }
};

const updateFlexBenefitActivation: ActivationDialogExtensions<FlexBenefitOptions>['onUpdateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
  context,
) => {
  const update: UpdateBenefitActivationDTO = {
    ...baseValues,
    flexOptions: mapFlexOptions(benefitOptions, context?.tenant),
  };
  await saveActivation(update);

  if (context?.tenant?.lunchTaxationType !== benefitOptions.lunchTaxationType && context?.tenant) {
    await apis.tenants.updateTenant({
      tenantId: context.tenant.id,
      updateTenantDTO: { lunchTaxationType: benefitOptions.lunchTaxationType },
    });
    await context.queryClient.invalidateQueries({ queryKey: ['tenants', context.tenant.id] });
  }
};

export class FlexBenefitActivationDialogExtension extends BenefitActivationDialogExtensionInterface<FlexBenefitOptions> {
  CreateFields?: React.ReactNode = (<Fields />);
  ChangeOptionsFields?: React.ReactNode = (<Fields />);
  Header?: React.ComponentType = Header;
  InlineField?: React.ComponentType = InlineField;

  onCreateActivation = createFlexBenefitActivation;
  onUpdateActivation = updateFlexBenefitActivation;
  onResetBenefitOptions? = resetFlexBenefitOptions;
}
